import {ChartData, ChartDataset, ChartOptions, ScatterDataPoint,} from 'chart.js';
import 'chartjs-adapter-date-fns';
import {useEffect, useState} from "react";
import {Chart, ChartProps} from '../Chart/Chart';
import {useTranslation} from "react-i18next";
import {
  CargoSubtype,
  ContainerLevelHistoryEntity, FeetInches,
  getNumberSorter,
  inchesToFeet,
  LevelType
} from "@ots-frontend/ots-data";
import {TFunction} from "i18next";
import {getCargoSubtypeColor} from "../../../util/cargo-subtype-ui-util";
import {TooltipOptions} from "chart.js/dist/types";

export type ContainerLevelChartProps = Omit<ChartProps<'line'>, 'type' | 'options' | 'data'> & {
  levels: ContainerLevelHistoryEntity[],
  type?: LevelType,
  max?: number
};

export const ContainerLevelChart = (props: ContainerLevelChartProps) => {
  const {levels, max, type = "volume", ...rest} = props;

  const {t,} = useTranslation(["common"])

  const [chartOptions, setChartOptions] = useState<
    ChartOptions<'line'> | undefined
  >(undefined);

  const [chartData, setChartData] = useState<
    ChartData<'line'> | undefined
  >(undefined);

  useEffect(() => {
    setChartOptions(getChartOptions(t, type, max))
  }, [t, type, max])

  useEffect(() => {
    setChartData(getChartData(levels, type, t))
  }, [levels, type, t])

  return <Chart {...rest} type="line" options={chartOptions} data={chartData}/>
};


const getChartOptions = (t: TFunction<"common"[], any, "common"[]>, type: LevelType, max: number | undefined): ChartOptions<'line'> => {
  let yText : string = t<string>("commons.entity.containerlevelhistory.chart.yaxis.label.volume", "Volume")
  if (type === "height") {
    yText = t<string>("commons.entity.containerlevelhistory.chart.yaxis.label.level.feet", "Level (Feet)")
  }

  return {
    maintainAspectRatio: true,
    animation: false,
    scales: {
      x: {
        title: {
          display: true,
          text: t<string>("commons.entity.containerlevelhistory.chart.xaxis.label", "Time"),
        },
        type: 'time',
        time: {
          unit: 'hour',
        },
      },
      y: {
        title: {
          display: true,
          text: yText,
        },
        beginAtZero: true,
        suggestedMax: max
      },
    },
    plugins: {
      datalabels: {
        display: false,
      },
      tooltip: type === "height" ? {
        callbacks: {
          label: function (tooltipItem) {
            const feetInches = FeetInches.fromFeet(tooltipItem.parsed.y)
            return feetInches.toString()
          }
        }
      } : undefined
    }
  };
};

const getChartData = (
  levels: ContainerLevelHistoryEntity[],
  levelType: LevelType,
  t: TFunction<"common"[], any, "common"[]>,
): ChartData<'line'> => {
  const chartDataSets: ChartDataset<'line'>[] = [];

  const groupedLevels = groupLevelsByCargoSubtype(levels)

  groupedLevels.forEach((groupedLevels, cargoSubtype) => {
    chartDataSets.push({
      label: t<string>(cargoSubtype.displayValue.id, cargoSubtype.displayValue.defaultMessage),
      fill: false,
      data: getChartPoints(groupedLevels, levelType),
      borderColor: getCargoSubtypeColor(cargoSubtype),
      borderWidth: 3,
    });
  })

  return {
    datasets: chartDataSets,
  };
};

const getChartPoints = (levels: ContainerLevelHistoryEntity[] | undefined, levelType: LevelType): ScatterDataPoint[] => {
  if (!levels)
    return []

  return levels
    .filter((l) => typeof l.getLevel(levelType) !== 'undefined')
    .sort(getNumberSorter('createDttm'))
    .map((l) => {
      return {
        x: l.createDttm!,
        y: l.getLevel(levelType, "feet")!,
      };
    });
};

const groupLevelsByCargoSubtype = (levels: ContainerLevelHistoryEntity[]): Map<CargoSubtype, ContainerLevelHistoryEntity[]> => {
  const groupedLevels = new Map<CargoSubtype, ContainerLevelHistoryEntity[]>();

  levels.forEach(lvl => {
    let group = groupedLevels.get(lvl.cargoSubtype)
    if (!group) {
      group = []
      groupedLevels.set(lvl.cargoSubtype, group)
    }

    group.push(lvl)
  })

  return groupedLevels;
}
